import { Button } from '@/components/common/Button';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import InputField from '@/components/common/InputField';
import Modal from '@/components/common/modals/Modal';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { useToasts } from '@/components/common/Toasts';
import useAutofocus from '@/components/common/useAutofocus';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { logError } from '@/util/logger';
import { useMutation } from '@apollo/client';
import { Circle, FormLabel } from '@chakra-ui/react';
import { TAG_COLORS } from '@common/cashflow';
import {
    CreateTransactionEntryTagDocument,
    UpdateTransactionEntryTagDocument,
} from '@common/graphql/generated';
import { TransactionEntryTag, useTags } from '@common/react/cashflow';
import { TOASTS } from '@common/toasts';
import React, { useState } from 'react';

interface TagModalProps {
    existingTag?: TransactionEntryTag;
    onSuccess?: (tag: { id: string; name: string; color: string }) => void;
}

export const showTagModal = (props: TagModalProps) =>
    ModalManager.show(TagModal, props);

const TagModal = ModalManager.create<TagModalProps>(
    ({ existingTag, onSuccess }) => {
        useTrackModalShown('Transaction Entry Tag');

        const modal = useModal();
        const [tagName, setTagName] = useState<string | null | undefined>(
            existingTag?.name
        );
        const [tagColor, setTagColor] = useState<string | null | undefined>(
            existingTag?.color
        );
        const [isSubmitting, setIsSubmitting] = useState(false);
        const { tags, loading } = useTags();
        const [createTag] = useMutation(CreateTransactionEntryTagDocument);
        const [updateTag] = useMutation(UpdateTransactionEntryTagDocument);
        const { showErrorToast, showSuccessToast } = useToasts();
        const autofocusRef = useAutofocus({ delay: 1000 });
        const isDisabled = !tagName || !tagColor || loading;

        const submit = async () => {
            if (isDisabled || isSubmitting) return;

            if (!existingTag && tags.some((tag) => tag.name === tagName)) {
                showErrorToast(TOASTS.tagNameAlreadyExists);
                setIsSubmitting(false);
                return;
            }

            try {
                if (existingTag && existingTag.id) {
                    const { data } = await updateTag({
                        variables: {
                            id: existingTag.id,
                            name: tagName,
                            color: tagColor,
                        },
                    });

                    if (data?.updateTransactionEntryTag?.transactionEntryTag) {
                        const { id, name, color } =
                            data.updateTransactionEntryTag.transactionEntryTag;

                        onSuccess?.({ id, name, color });
                    }
                } else {
                    const { data } = await createTag({
                        variables: {
                            name: tagName,
                            color: tagColor,
                        },
                    });

                    if (data?.createTransactionEntryTag?.transactionEntryTag) {
                        const { id, name, color } =
                            data.createTransactionEntryTag.transactionEntryTag;

                        onSuccess?.({ id, name, color });
                    }
                }

                showSuccessToast(
                    TOASTS.createOrUpdateTagSuccess({ tagName, existingTag })
                );

                modal.remove();
            } catch (e) {
                logError(e);
                showErrorToast(
                    TOASTS.createOrUpdateTagError({
                        existingTag,
                    })
                );
            } finally {
                setIsSubmitting(false);
            }
        };

        const labelProps = { fontSize: 'md' };

        return (
            <Modal
                isOpen={modal.visible}
                onClose={modal.remove}
                closeOnOverlayClick={false}
                size="xl"
                closeOnEsc={false}
                isCentered={true}
                modalContentProps={{ h: '90vh', my: 8 }}
                title={existingTag ? 'Edit Tag' : 'Create Tag'}
                renderFooter={() => (
                    <FlexRow justifyContent="flex-end" px={10} py={5}>
                        <Button
                            data-cy="save-tag-button"
                            onClickEvent="Save Tag Button Clicked"
                            onClickEventParams={{ tag_name: tagName }}
                            onClick={submit}
                            isLoading={isSubmitting}
                            isDisabled={isDisabled}
                        >
                            Save
                        </Button>
                    </FlexRow>
                )}
            >
                <FlexCol gap={7}>
                    <InputField
                        name="tagName"
                        ref={autofocusRef}
                        labelProps={labelProps}
                        placeholder="Enter tag name..."
                        label="Tag name"
                        type="text"
                        inputProps={{
                            variant: 'outline',
                            size: 'md',
                        }}
                        value={tagName}
                        onChange={setTagName}
                    />
                    <FlexCol>
                        <FormLabel {...labelProps}>Color</FormLabel>
                        <FlexRow gap={3} justify="space-between" align="center">
                            {TAG_COLORS.map((color) => (
                                <Circle
                                    key={color}
                                    bg={color}
                                    size={9}
                                    border="2px solid"
                                    borderColor={
                                        tagColor === color
                                            ? 'black'
                                            : 'transparent'
                                    }
                                    {...hoverStyleProps({
                                        transform: 'translateY(-2px)',
                                    })}
                                    onClick={() => setTagColor(color)}
                                    cursor="pointer"
                                    data-cy="tag-color"
                                />
                            ))}
                        </FlexRow>
                    </FlexCol>
                </FlexCol>
            </Modal>
        );
    }
);
